import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useStyles } from './styles';
import {
    Box,
    Button,
    Chip,
    Collapse,
    Grid,
    IconButton,
    Modal,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    KeyboardArrowDown,
    KeyboardArrowUp,
    Code,
    Image as ImageIcon,
    OpenInNew,
} from '@mui/icons-material';
import { IStore } from '../../store/types';
import dayjs from 'dayjs';

interface ModelFrontLog {
    id?: number | string;
    device: string;
    imageUrl?: string;
    logData: {
        sessionId: string;
        backend: string;
        detectedPillows: number;
        errors: any[];
        modelVersion: string;
        perf: {
            preprocess: number;
            inference: number;
            postprocess: number;
            render: number;
        };
        results: Array<{ orderNumber: string; score: number; client: string }>;
        searchDurationMs: number;
        searchImageB64?: string;
        thresholds: { conf: number; iou: number; mask: number };
        user?: { name: string; role: string; allRoles?: string[] };
    };
    createdAt?: string;
}

interface RowProps {
    log: ModelFrontLog;
    index: number;
}

function ScoreBar({ score }: { score: number }) {
    const color = score >= 0.9 ? '#4caf50' : score >= 0.8 ? '#ff9800' : '#f44336';
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
                width: 60,
                height: 6,
                bgcolor: 'action.hover',
                borderRadius: 3,
                overflow: 'hidden',
            }}>
                <Box sx={{ width: `${score * 100}%`, height: '100%', bgcolor: color, borderRadius: 3 }} />
            </Box>
            <Typography variant="caption" sx={{ color, fontWeight: 'bold', minWidth: 38 }}>
                {(score * 100).toFixed(1)}%
            </Typography>
        </Box>
    );
}

function ImageThumb({ src, label, isBase64 }: { src?: string; label: string; isBase64?: boolean }) {
    const theme = useTheme();
    if (!src) {
        return (
            <Box sx={{
                width: 48, height: 48, borderRadius: 1,
                bgcolor: theme.palette.action.hover,
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.6rem' }}>
                    {label}
                </Typography>
            </Box>
        );
    }
    const imgSrc = isBase64 ? `data:image/png;base64,${src}` : src;
    return (
        <Tooltip
            placement="right"
            title={
                <Box sx={{ p: 0.5 }}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'white', fontWeight: 'bold' }}>{label}</Typography>
                    <img src={imgSrc} alt={label} style={{ maxWidth: 260, maxHeight: 260, objectFit: 'contain', display: 'block', borderRadius: 4 }} />
                </Box>
            }
            componentsProps={{ tooltip: { sx: { bgcolor: 'rgba(0,0,0,0.88)', maxWidth: 300, p: 1 } } }}
        >
            <Box sx={{ cursor: 'zoom-in' }}>
                <img
                    src={imgSrc}
                    alt={label}
                    style={{
                        width: 48, height: 48,
                        objectFit: 'cover',
                        borderRadius: 4,
                        border: `1px solid`,
                        borderColor: 'divider',
                        display: 'block',
                    }}
                />
            </Box>
        </Tooltip>
    );
}

function PerfCard({ label, value }: { label: string; value: number }) {
    const theme = useTheme();
    return (
        <Box sx={{ p: '10px 14px', bgcolor: theme.palette.action.hover, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>{label}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{value?.toFixed(1) ?? '—'} ms</Typography>
        </Box>
    );
}

function Row({ log, index }: RowProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [rawOpen, setRawOpen] = useState(false);
    const [imageOpen, setImageOpen] = useState(false);
    const classes = useStyles();

    const { logData } = log;
    const perf = logData?.perf || {};
    const results = logData?.results || [];
    const errors = logData?.errors || [];
    const hasErrors = errors.length > 0;

    const deviceShort = log.device?.split('|')[0]?.trim() || '—';
    const screenInfo = log.device?.split('|')[1]?.trim() || '';

    return (
        <>
            <TableRow
                hover
                sx={{
                    '& > *': { borderBottom: 'unset' },
                    bgcolor: hasErrors ? (theme.palette.mode === 'dark' ? 'rgba(244,67,54,0.08)' : '#fff5f5') : 'inherit',
                }}
            >
                <TableCell sx={{ width: 40, p: '4px 8px' }}>
                    <IconButton size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                <TableCell sx={{ maxWidth: 200 }}>
                    <Tooltip title={log.device || ''} placement="top">
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{deviceShort}</Typography>
                    </Tooltip>
                    {screenInfo && (
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>{screenInfo}</Typography>
                    )}
                </TableCell>
                <TableCell sx={{ minWidth: 110 }}>
                    {logData?.user?.name ? (
                        <>
                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                {logData.user.name}
                            </Typography>
                            <Chip label={logData.user.role} size="small" variant="outlined" sx={{ mt: 0.3, height: 18, fontSize: '0.65rem' }} />
                        </>
                    ) : (
                        <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>—</Typography>
                    )}
                </TableCell>
                <TableCell sx={{ p: '4px 8px' }}>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <ImageThumb
                            src={logData?.searchImageB64}
                            label="Search (query)"
                            isBase64
                        />
                        <Box sx={{ color: 'text.disabled', fontSize: 14, lineHeight: 1 }}>→</Box>
                        <ImageThumb
                            src={log.imageUrl}
                            label="Input image"
                        />
                    </Box>
                </TableCell>
                <TableCell>
                    <Chip label={logData?.backend || '—'} size="small" color="primary" variant="outlined" />
                </TableCell>
                <TableCell align="center">
                    <Chip
                        label={logData?.detectedPillows ?? '—'}
                        size="small"
                        color={logData?.detectedPillows > 0 ? 'success' : 'default'}
                    />
                </TableCell>
                <TableCell align="right">{perf.inference?.toFixed(1) ?? '—'} ms</TableCell>
                <TableCell align="right">{perf.preprocess?.toFixed(1) ?? '—'} ms</TableCell>
                <TableCell align="right">{perf.render?.toFixed(1) ?? '—'} ms</TableCell>
                <TableCell align="right">{logData?.searchDurationMs ?? '—'} ms</TableCell>
                <TableCell>
                    {hasErrors ? (
                        <Chip label={`${errors.length} error${errors.length > 1 ? 's' : ''}`} size="small" color="error" />
                    ) : (
                        <Chip label="OK" size="small" color="success" />
                    )}
                </TableCell>
                <TableCell>
                    {log.createdAt ? dayjs(log.createdAt).format('MM-DD HH:mm:ss') : '—'}
                </TableCell>
                <TableCell sx={{ p: '4px 8px' }}>
                    {log.id && (
                        <Tooltip title={`Open log #${log.id}`}>
                            <IconButton size="small" onClick={() => navigate(`/model-front-logs/${log.id}`)}>
                                <OpenInNew fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </TableCell>
            </TableRow>

            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={13}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ m: 2 }}>
                            {/* Performance */}
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, borderBottom: `2px solid ${theme.palette.primary.main}`, pb: 0.5, mb: 1.5 }}>
                                Performance
                            </Typography>
                            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                                <Grid item xs={6} sm={3}><PerfCard label="Preprocess" value={perf.preprocess} /></Grid>
                                <Grid item xs={6} sm={3}><PerfCard label="Inference" value={perf.inference} /></Grid>
                                <Grid item xs={6} sm={3}><PerfCard label="Postprocess" value={perf.postprocess} /></Grid>
                                <Grid item xs={6} sm={3}><PerfCard label="Render" value={perf.render} /></Grid>
                            </Grid>

                            {/* Session Info */}
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, borderBottom: `2px solid ${theme.palette.primary.main}`, pb: 0.5, mb: 1.5 }}>
                                Session Info
                            </Typography>
                            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ p: 1.5, bgcolor: theme.palette.action.hover, borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
                                        <Typography variant="body2"><strong>Session ID:</strong> {logData?.sessionId || '—'}</Typography>
                                        {logData?.user && (
                                            <Typography variant="body2"><strong>User:</strong> {logData.user.name} ({logData.user.role})</Typography>
                                        )}
                                        <Typography variant="body2"><strong>Backend:</strong> {logData?.backend || '—'}</Typography>
                                        <Typography variant="body2"><strong>Model:</strong>{' '}
                                            <Tooltip title={logData?.modelVersion || ''}>
                                                <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                                                    {logData?.modelVersion?.split('/').pop() || '—'}
                                                </span>
                                            </Tooltip>
                                        </Typography>
                                        <Typography variant="body2"><strong>Detected Pillows:</strong> {logData?.detectedPillows}</Typography>
                                        <Typography variant="body2"><strong>Search Duration:</strong> {logData?.searchDurationMs} ms</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ p: 1.5, bgcolor: theme.palette.action.hover, borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
                                        <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Thresholds</strong></Typography>
                                        <Typography variant="body2">Conf: {logData?.thresholds?.conf}</Typography>
                                        <Typography variant="body2">IoU: {logData?.thresholds?.iou}</Typography>
                                        <Typography variant="body2">Mask: {logData?.thresholds?.mask}</Typography>
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* Device */}
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, borderBottom: `2px solid ${theme.palette.primary.main}`, pb: 0.5, mb: 1.5 }}>
                                Device
                            </Typography>
                            <Box sx={{ p: 1.5, bgcolor: theme.palette.action.hover, borderRadius: 1, border: `1px solid ${theme.palette.divider}`, mb: 2, fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-word' }}>
                                {log.device || '—'}
                            </Box>

                            {/* Results */}
                            {results.length > 0 && (
                                <>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, borderBottom: `2px solid ${theme.palette.primary.main}`, pb: 0.5, mb: 1.5 }}>
                                        Results ({results.length})
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 260 }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>#</TableCell>
                                                    <TableCell>Order Number</TableCell>
                                                    <TableCell>Client</TableCell>
                                                    <TableCell>Score</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {results.map((r, i) => (
                                                    <TableRow key={i} hover>
                                                        <TableCell>{i + 1}</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>{r.orderNumber}</TableCell>
                                                        <TableCell>
                                                            <Chip label={r.client} size="small" variant="outlined" />
                                                        </TableCell>
                                                        <TableCell>
                                                            <ScoreBar score={r.score} />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}

                            {/* Errors */}
                            {hasErrors && (
                                <>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'error.main', borderBottom: `2px solid`, borderColor: 'error.main', pb: 0.5, mb: 1.5 }}>
                                        Errors
                                    </Typography>
                                    <Box sx={{ p: 1.5, bgcolor: '#ffebee', borderRadius: 1, mb: 2 }}>
                                        {errors.map((err, i) => (
                                            <Typography key={i} variant="body2" color="error">
                                                {typeof err === 'string' ? err : JSON.stringify(err)}
                                            </Typography>
                                        ))}
                                    </Box>
                                </>
                            )}

                            {/* Image + Raw JSON buttons */}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {(log.imageUrl || log.logData?.searchImageB64) && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<ImageIcon />}
                                        onClick={() => setImageOpen(true)}
                                    >
                                        View Image
                                    </Button>
                                )}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Code />}
                                    onClick={() => setRawOpen(true)}
                                >
                                    Raw JSON
                                </Button>
                            </Box>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>

            {/* Image Modal */}
            <Modal open={imageOpen} onClose={() => setImageOpen(false)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, p: 3, maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: theme.palette.primary.main }}>Search Image</Typography>
                        <IconButton onClick={() => setImageOpen(false)}><KeyboardArrowUp /></IconButton>
                    </Box>
                    {log.imageUrl && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>Input Image</Typography>
                            <img src={log.imageUrl} alt="Input" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 6 }} />
                        </Box>
                    )}
                    {log.logData?.searchImageB64 && (
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Search Image (searchImageB64)</Typography>
                            <img
                                src={`data:image/png;base64,${log.logData.searchImageB64}`}
                                alt="Search"
                                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 6 }}
                            />
                        </Box>
                    )}
                </Box>
            </Modal>

            {/* Raw JSON Modal */}
            <Modal open={rawOpen} onClose={() => setRawOpen(false)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, p: 3, maxWidth: '95vw', maxHeight: '95vh', overflow: 'auto', width: 900 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: theme.palette.primary.main }}>Raw Log Data</Typography>
                        <IconButton onClick={() => setRawOpen(false)}><KeyboardArrowUp /></IconButton>
                    </Box>
                    <Box sx={{ bgcolor: theme.palette.action.hover, p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', overflow: 'auto', maxHeight: '80vh', wordBreak: 'break-all' }}>
                        <pre style={{ margin: 0, color: theme.palette.text.primary }}>
                            {JSON.stringify({
                                ...log,
                                logData: {
                                    ...log.logData,
                                    searchImageB64: log.logData?.searchImageB64 ? '[truncated — base64]' : undefined,
                                },
                            }, null, 2)}
                        </pre>
                    </Box>
                </Box>
            </Modal>
        </>
    );
}

interface Props {
    onPageChange: (event: unknown, newPage: number) => void;
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export default function ModelFrontLogsTable({ onPageChange, onRowsPerPageChange }: Props) {
    const theme = useTheme();
    const { data, status, queryParams } = useSelector<IStore, { data: any; status: string | null; queryParams: any }>(
        (state) => ({
            data: state.modelFrontLogs.data,
            status: state.modelFrontLogs.status,
            queryParams: state.modelFrontLogs.queryParams,
        })
    );
    const classes = useStyles();

    if (status === 'loading') {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <Typography color="text.secondary">Loading…</Typography>
            </Box>
        );
    }

    if (status === 'failed') {
        return (
            <Box sx={{ p: 3, bgcolor: '#ffebee', borderRadius: 1 }}>
                <Typography color="error">Failed to load data. Check your connection or date range.</Typography>
            </Box>
        );
    }

    const logs: ModelFrontLog[] = data?.result || [];
    const total = data?.totalCount || 0;
    const currentPage = (queryParams?.page || 1) - 1;
    const rowsPerPage = queryParams?.limit || 50;

    return (
        <Paper className={classes.tableContainer} elevation={1}>
            <TableContainer>
                <Table stickyHeader size="small" aria-label="model front logs table">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 40 }} />
                            <TableCell sx={{ minWidth: 160 }}>Device</TableCell>
                            <TableCell sx={{ minWidth: 110 }}>User</TableCell>
                            <TableCell sx={{ minWidth: 116 }}>Images</TableCell>
                            <TableCell sx={{ minWidth: 100 }}>Backend</TableCell>
                            <TableCell align="center" sx={{ minWidth: 80 }}>Pillows</TableCell>
                            <TableCell align="right" sx={{ minWidth: 100 }}>Inference</TableCell>
                            <TableCell align="right" sx={{ minWidth: 110 }}>Preprocess</TableCell>
                            <TableCell align="right" sx={{ minWidth: 80 }}>Render</TableCell>
                            <TableCell align="right" sx={{ minWidth: 110 }}>Search</TableCell>
                            <TableCell sx={{ minWidth: 80 }}>Status</TableCell>
                            <TableCell sx={{ minWidth: 130 }}>Created At</TableCell>
                            <TableCell sx={{ width: 48 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {logs.length === 0 && status === 'succeeded' ? (
                            <TableRow>
                                <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">No logs found for the selected date range.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            logs.map((log, index) => <Row key={log.logData?.sessionId || index} log={log} index={index} />)
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[25, 50, 100, 200, 500]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={currentPage}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
            />
        </Paper>
    );
}
